"use client";

import type { FormEvent } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { KundaliLoadingSteps } from "./KundaliFormCard.loading";
import { FormErrorMessage, KundaliSubmitButton } from "./KundaliFormCard.controls";
import {
  NameField,
  BirthDateField,
  BirthTimeField,
  BirthPlaceField,
} from "./KundaliFormCard.fields";
import type { SEOIntentConfig } from "@/lib/seo-intents";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";

export type CapturedSEOEntry = {
  config: SEOIntentConfig;
  sourcePath: string;
};

export function KundaliInputForm({
  containerClassName,
  onSubmit,
  seoEntry,
  language,
  setLanguage,
  name,
  setName,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  activeField,
  setActiveField,
  markFormStarted,
  fieldErrors,
  error,
  loading,
  loadingStep,
  reassuranceNote,
  deliveryField,
  t,
}: {
  containerClassName: string;
  onSubmit: (event: FormEvent) => void;
  seoEntry: CapturedSEOEntry | null;
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
  name: string;
  setName: (value: string) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  fieldErrors: KundaliFieldErrors;
  error: string | null;
  loading: boolean;
  loadingStep: number;
  reassuranceNote?: string;
  deliveryField?: import("react").ReactNode;
  t: ReturnType<typeof getKundaliTranslations>;
}) {
  return (
    <div className={containerClassName}>
      <form onSubmit={onSubmit} noValidate className="card-sacred p-6 sm:p-8 space-y-5">
        <SEOIntentBanner seoEntry={seoEntry} />
        <LanguageToggle language={language} setLanguage={setLanguage} />
        <InputReassurance note={reassuranceNote} />
        <KundaliPersonalFields
          {...{
            name,
            setName,
            birthDate,
            setBirthDate,
            birthTime,
            setBirthTime,
            requireBirthTime,
            activeField,
            setActiveField,
            markFormStarted,
            language,
            t,
            fieldErrors,
          }}
        />
        <KundaliBirthPlaceField
          {...{
            birthPlace,
            setBirthPlace,
            setSelectedPlace,
            activeField,
            setActiveField,
            markFormStarted,
            language,
            t,
            fieldErrors,
          }}
        />
        <FormErrorMessage error={error} />
        {deliveryField}
        <KundaliSubmitButton
          loading={loading}
          label={seoEntry?.config?.ctaLabel || t.submit}
          loadingLabel={t.loading}
        />
        {loading && <KundaliLoadingSteps loadingStep={loadingStep} language={language} />}
        <p className="text-[11px] text-center" style={{ color: "var(--ish-text-faint)" }}>
          {t.savedNote}
        </p>
      </form>
    </div>
  );
}

function InputReassurance({ note }: { note?: string }) {
  if (!note) return null;
  return (
    <p className="rounded-md px-3 py-2 text-center text-[12px]" style={reassuranceStyle}>
      {note}
    </p>
  );
}

const reassuranceStyle = {
  background: "var(--ish-bg)",
  border: "1px solid var(--ish-border)",
  color: "var(--ish-text-muted)",
};

function SEOIntentBanner({ seoEntry }: { seoEntry: CapturedSEOEntry | null }) {
  if (!seoEntry) return null;
  return (
    <div
      className="rounded-lg p-3.5 text-[13px]"
      style={{
        background: "var(--ish-accent-light)",
        border: "1px solid var(--ish-accent-border)",
        color: "var(--ish-text-muted)",
      }}
    >
      <p
        className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={{ color: "var(--ish-accent)", letterSpacing: "0.12em" }}
      >
        <span aria-hidden>ॐ</span> Pandit Aaditya · Your question
      </p>
      <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
        {seoEntry.config.prompt}
      </p>
      <p className="mt-1 text-[12px]">
        I&apos;ll read your chart to answer this. Share your birth details below — your question
        stays open and I&apos;ll continue it the moment your kundali is ready.
      </p>
    </div>
  );
}

function LanguageToggle({
  language,
  setLanguage,
}: {
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
}) {
  return (
    <div className="flex justify-center">
      <div
        role="tablist"
        aria-label="Language"
        className="flex p-1 rounded-full"
        style={{ background: "var(--ish-bg)", border: "1px solid var(--ish-border)" }}
      >
        {(["en", "hi"] as KundaliLang[]).map((lang) => (
          <button
            key={lang}
            type="button"
            role="tab"
            aria-selected={language === lang}
            onClick={() => setLanguage(lang)}
            className="px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-200"
            style={{
              color: language === lang ? "#fff" : "var(--ish-text-muted)",
              backgroundColor: language === lang ? "var(--ish-accent)" : "transparent",
            }}
          >
            {lang === "en" ? "English" : "हिंदी"}
          </button>
        ))}
      </div>
    </div>
  );
}

function KundaliPersonalFields({
  name,
  setName,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  activeField,
  setActiveField,
  markFormStarted,
  language,
  t,
  fieldErrors,
}: {
  name: string;
  setName: (value: string) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  language: KundaliLang;
  t: ReturnType<typeof getKundaliTranslations>;
  fieldErrors: KundaliFieldErrors;
}) {
  return (
    <>
      <NameField
        value={name}
        onChange={setName}
        onFocus={() => markFormStarted("name")}
        onBlur={() => setActiveField(null)}
        activeField={activeField}
        language={language}
        t={t}
        error={fieldErrors.name}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <BirthDateField
          value={birthDate}
          onChange={setBirthDate}
          onFocus={() => markFormStarted("birthDate")}
          onBlur={() => setActiveField(null)}
          activeField={activeField}
          language={language}
          t={t}
          error={fieldErrors.birthDate}
        />
        <BirthTimeField
          value={birthTime}
          onChange={setBirthTime}
          onFocus={() => markFormStarted("birthTime")}
          onBlur={() => setActiveField(null)}
          activeField={activeField}
          language={language}
          t={t}
          required={requireBirthTime}
          error={fieldErrors.birthTime}
        />
      </div>
    </>
  );
}

function KundaliBirthPlaceField({
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  activeField,
  setActiveField,
  markFormStarted,
  language,
  t,
  fieldErrors,
}: {
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  language: KundaliLang;
  t: ReturnType<typeof getKundaliTranslations>;
  fieldErrors: KundaliFieldErrors;
}) {
  return (
    <BirthPlaceField
      value={birthPlace}
      onChange={(val) => {
        setBirthPlace(val);
        setSelectedPlace(null);
      }}
      onSelect={(place) => {
        setBirthPlace(place.display);
        setSelectedPlace(place);
      }}
      onFocus={() => markFormStarted("birthPlace")}
      onBlur={() => setActiveField(null)}
      activeField={activeField}
      language={language}
      t={t}
      error={fieldErrors.birthPlace}
    />
  );
}
